import type { ReactElement, KeyboardEvent, ChangeEvent } from "react";
import type { StoryVersion } from "@/types/story";

import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  Globe,
  Lock,
  Trash2,
  Loader2,
  Image,
  Wand2,
  Calendar,
  Palette,
  Pencil,
  Check,
  X,
  Star,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "@/constants/categories";
import VersionBadge from "@/components/VersionBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Story page structure
 */
interface StoryPage {
  page_number: number;
  text: string;
  image_prompt: string;
  image_url?: string;
  audio_url?: string;
}

/**
 * Story data for settings modal
 */
interface Story {
  id: string;
  title: string;
  prompt?: string;
  age_range: string;
  category?: string;
  art_style?: string;
  visual_preset?: string;
  is_public?: boolean;
  thumbnail?: string;
  pages?: StoryPage[];
  version?: StoryVersion;
  is_pro_story?: boolean;
  created_at: string;
  regeneration_count?: number;
}

/**
 * Maximum thumbnail regenerations allowed per story
 */
const THUMBNAIL_REGENERATION_LIMIT = 2;

/**
 * Visual preset to display label mapping
 */
const VISUAL_PRESET_LABELS: Record<string, string> = {
  "classic-storybook": "Classic Storybook",
  "soft-watercolor": "Soft Watercolor",
  "comic-adventure": "Comic Adventure",
  "modern-minimalist": "Modern Minimalist",
  "dreamy-pastel": "Dreamy Pastel",
  "realistic-painted": "Realistic Painted",
  "cinematic-3d": "Cinematic 3D",
  "biblical-epic": "Biblical Epic",
};

/**
 * Art style to display label mapping (fallback for older stories)
 */
const ART_STYLE_LABELS: Record<string, string> = {
  storybook: "Classic Storybook",
  watercolor: "Watercolor",
  cartoon: "Cartoon",
  "3d-render": "3D Render",
  anime: "Anime",
  "pencil-sketch": "Pencil Sketch",
};

/**
 * Get display label for art style
 */
const getArtStyleLabel = (story: Story): string => {
  // Prefer visual_preset if available
  if (story.visual_preset && VISUAL_PRESET_LABELS[story.visual_preset]) {
    return VISUAL_PRESET_LABELS[story.visual_preset];
  }
  // Fall back to art_style
  if (story.art_style && ART_STYLE_LABELS[story.art_style]) {
    return ART_STYLE_LABELS[story.art_style];
  }
  // Last resort: format the raw value or show default
  if (story.art_style) {
    return story.art_style.replace(/_/g, " ").replace(/-/g, " ");
  }
  return "Classic Storybook";
};

/**
 * Props for StorySettingsModal component
 */
interface StorySettingsModalProps {
  story: Story | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (story: Story) => void;
  onDelete?: (storyId: string) => void;
}

/**
 * Editable field type
 */
type EditableField = "title" | "category" | null;

/**
 * Thumbnail regeneration response
 */
interface ThumbnailResponse {
  thumbnail: string;
}

/**
 * Helper to get full image URL
 */
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * StorySettingsModal Component
 *
 * Modal for viewing and editing story settings including
 * title, category, visibility, and thumbnail regeneration.
 */
const StorySettingsModal = ({
  story,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
}: StorySettingsModalProps): ReactElement | null => {
  const [isPublic, setIsPublic] = useState<boolean>(story?.is_public ?? true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [regeneratingThumbnail, setRegeneratingThumbnail] =
    useState<boolean>(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState<boolean>(false);
  const [regenerationCount, setRegenerationCount] = useState<number>(story?.regeneration_count || 0);

  // Editing state for title and category
  const [editingField, setEditingField] = useState<EditableField>(null);
  const [editedTitle, setEditedTitle] = useState<string>("");
  const [editedCategory, setEditedCategory] = useState<string>("");
  const [savingField, setSavingField] = useState<boolean>(false);

  // Regeneration limit calculations
  const regenerationsRemaining = THUMBNAIL_REGENERATION_LIMIT - regenerationCount;
  const canRegenerate = regenerationsRemaining > 0;

  useEffect(() => {
    if (story) {
      setIsPublic(story.is_public ?? true);
      setEditingField(null);
      setDeleting(false);
      setRegeneratingThumbnail(false);
      setShowRegenerateConfirm(false);
      setRegenerationCount(story.regeneration_count || 0);
    }
  }, [story]);

  const handleVisibilityChange = async (checked: boolean): Promise<void> => {
    if (!story) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/stories/${story.id}`,
        { is_public: checked },
        { withCredentials: true }
      );

      setIsPublic(checked);
      toast.success(checked ? "Story is now public" : "Story is now private");
      onUpdate?.({ ...story, is_public: checked });
    } catch (error) {
      console.error("Failed to update visibility:", error);
      toast.error("Failed to update visibility");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (field: "title" | "category"): void => {
    if (!story) return;
    if (field === "title") {
      setEditedTitle(story.title || "");
    } else if (field === "category") {
      setEditedCategory(story.category || "");
    }
    setEditingField(field);
  };

  const handleSaveField = async (field: "title" | "category"): Promise<void> => {
    if (savingField || !story) return;

    const value = field === "title" ? editedTitle : editedCategory;

    if (field === "title" && !value.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    if (field === "title" && value.trim() === story.title) {
      setEditingField(null);
      return;
    }

    if (field === "category" && value === story.category) {
      setEditingField(null);
      return;
    }

    setSavingField(true);
    try {
      const updateData =
        field === "title" ? { title: value.trim() } : { category: value };

      await axios.patch(`${API}/stories/${story.id}`, updateData, {
        withCredentials: true,
      });

      const updatedStory: Story = {
        ...story,
        ...(field === "title" ? { title: value.trim() } : { category: value }),
      };
      onUpdate?.(updatedStory);
      setEditingField(null);
      toast.success(`${field === "title" ? "Title" : "Category"} updated!`);
    } catch (error) {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      toast.error(
        axiosError.response?.data?.detail || `Failed to update ${field}`
      );
    } finally {
      setSavingField(false);
    }
  };

  const handleRegenerateThumbnail = async (): Promise<void> => {
    if (regeneratingThumbnail || !story || !canRegenerate) return;
    setShowRegenerateConfirm(false);
    setRegeneratingThumbnail(true);
    toast.info("Regenerating thumbnail...");

    try {
      const response = await axios.post<ThumbnailResponse>(
        `${API}/stories/${story.id}/regenerate-thumbnail`,
        {},
        { withCredentials: true }
      );

      const newRegenerationCount = regenerationCount + 1;
      setRegenerationCount(newRegenerationCount);
      onUpdate?.({ ...story, thumbnail: response.data.thumbnail, regeneration_count: newRegenerationCount });
      toast.success("Thumbnail regenerated!");
    } catch (error) {
      console.error("Failed to regenerate thumbnail:", error);
      const axiosError = error as { response?: { data?: { detail?: string } } };
      toast.error(
        axiosError.response?.data?.detail || "Failed to regenerate thumbnail"
      );
    } finally {
      setRegeneratingThumbnail(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!story) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/stories/${story.id}`, {
        withCredentials: true,
      });
      toast.success("Story deleted successfully");
      onDelete?.(story.id);
      onClose();
    } catch (error) {
      console.error("Failed to delete story:", error);
      toast.error("Failed to delete story");
      setDeleting(false);
    }
  };

  const handleTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") handleSaveField("title");
    if (e.key === "Escape") setEditingField(null);
  };

  if (!story) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden rounded-2xl"
        style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
      >
        {/* Header */}
        <div
          className="px-5 pt-5 pb-4"
          style={{ borderBottom: "1px solid var(--st-border-light)" }}
        >
          <div className="flex items-start gap-4">
            {/* Thumbnail */}
            <div
              className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0"
              style={{ border: "2px solid var(--st-border-light)" }}
            >
              {story.thumbnail ? (
                <img
                  src={getImageUrl(story.thumbnail) || undefined}
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)",
                  }}
                >
                  <Sparkles className="w-6 h-6" style={{ color: "var(--st-primary)" }} />
                </div>
              )}
            </div>

            {/* Title & Meta */}
            <div className="flex-1 min-w-0 overflow-hidden">
              {editingField === "title" ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setEditedTitle(e.target.value)
                    }
                    className="flex-1 min-w-0 text-base font-bold px-2 py-1 rounded-lg border-2 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500"
                    style={{
                      fontFamily: "Chewy, cursive",
                      color: "var(--st-text-heading)",
                      borderColor: "var(--st-border-light)",
                      background: "var(--st-bg-surface)",
                    }}
                    autoFocus
                    onKeyDown={handleTitleKeyDown}
                    disabled={savingField}
                  />
                  <button
                    onClick={() => handleSaveField("title")}
                    disabled={savingField}
                    className="p-1 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 flex-shrink-0"
                  >
                    {savingField ? (
                      <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                    ) : (
                      <Check className="w-4 h-4 text-green-600" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingField(null)}
                    disabled={savingField}
                    className="p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <h2
                      className="text-lg font-bold truncate"
                      style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                    >
                      {story.title}
                    </h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <VersionBadge
                        version={story.version}
                        isProStory={story.is_pro_story}
                        size="small"
                      />
                      <span
                        className="text-xs"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        {story.pages?.length || 0} pages
                      </span>
                      <span className="text-xs" style={{ color: "var(--st-text-muted)" }}>
                        •
                      </span>
                      <span
                        className="text-xs"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-muted)",
                        }}
                      >
                        {story.age_range}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => startEditing("title")}
                    className="p-1.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors flex-shrink-0 mr-6"
                  >
                    <Pencil
                      className="w-3.5 h-3.5"
                      style={{ color: "var(--st-primary)" }}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-3">
          {/* Category Row */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
              <span
                className="text-sm font-medium"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
              >
                Category
              </span>
            </div>
            {editingField === "category" ? (
              <div className="flex items-center gap-1">
                <select
                  value={editedCategory}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setEditedCategory(e.target.value)
                  }
                  className="text-sm px-2 py-1 rounded-lg border focus:outline-none focus:border-orange-400 dark:focus:border-orange-500"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                    borderColor: "var(--st-border-medium)",
                    background: "var(--st-bg-surface)",
                  }}
                  disabled={savingField}
                >
                  {CATEGORY_LABELS.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => handleSaveField("category")}
                  disabled={savingField}
                  className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                >
                  {savingField ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" />
                  ) : (
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => setEditingField(null)}
                  disabled={savingField}
                  className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEditing("category")}
                className="flex items-center gap-1 text-sm font-semibold px-2 py-1 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                <span className="capitalize">
                  {story.category || "General"}
                </span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Art Style Row */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
              <span
                className="text-sm font-medium"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
              >
                Art Style
              </span>
            </div>
            <span
              className="text-sm font-semibold"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              {getArtStyleLabel(story)}
            </span>
          </div>

          {/* Prompt Row */}
          {story.prompt && (
            <div className="py-2">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
                <span
                  className="text-sm font-medium"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-heading)",
                  }}
                >
                  Original Prompt
                </span>
              </div>
              <p
                className="text-sm leading-relaxed px-3 py-2 rounded-lg bg-orange-50/50 dark:bg-white/5"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                "{story.prompt}"
              </p>
            </div>
          )}

          {/* Visibility Toggle */}
          <div
            className={`flex items-center justify-between py-3 px-3 rounded-xl border ${
              isPublic
                ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50"
                : "bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isPublic ? "bg-green-500" : "bg-gray-400 dark:bg-gray-600"
                }`}
              >
                {isPublic ? (
                  <Globe className="w-4 h-4 text-white" />
                ) : (
                  <Lock className="w-4 h-4 text-white" />
                )}
              </div>
              <div>
                <p
                  className="text-sm font-semibold"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
                >
                  {isPublic ? "Public" : "Private"}
                </p>
                <p
                  className="text-xs"
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                >
                  {isPublic ? "Visible to everyone" : "Only you can see"}
                </p>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={handleVisibilityChange}
              disabled={saving}
              className="data-[state=checked]:bg-green-500"
            />
          </div>
        </div>

        {/* Actions Footer */}
        <div
          className="px-5 py-4 space-y-2 bg-gray-50/50 dark:bg-white/5"
          style={{ borderTop: "1px solid var(--st-border-light)" }}
        >
          {/* Regenerate Thumbnail */}
          <AlertDialog open={showRegenerateConfirm} onOpenChange={setShowRegenerateConfirm}>
            <AlertDialogTrigger asChild>
              <button
                disabled={regeneratingThumbnail || !canRegenerate}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
              >
                <div className="flex items-center gap-3">
                  {regeneratingThumbnail ? (
                    <Loader2
                      className="w-4 h-4 animate-spin"
                      style={{ color: "var(--st-primary)" }}
                    />
                  ) : (
                    <Image className="w-4 h-4" style={{ color: canRegenerate ? "var(--st-primary)" : "var(--st-text-muted)" }} />
                  )}
                  <div className="text-left">
                    <span
                      className="text-sm font-medium block"
                      style={{ fontFamily: "Quicksand, sans-serif", color: canRegenerate ? "var(--st-text-heading)" : "var(--st-text-muted)" }}
                    >
                      Regenerate Thumbnail
                    </span>
                    <span
                      className="text-xs"
                      style={{ fontFamily: "Quicksand, sans-serif", color: canRegenerate ? "var(--st-text-muted)" : "var(--st-text-locked)" }}
                    >
                      {canRegenerate
                        ? `${regenerationsRemaining} regeneration${regenerationsRemaining === 1 ? '' : 's'} remaining`
                        : "No regenerations remaining"
                      }
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: canRegenerate ? "var(--st-text-muted)" : "var(--st-text-locked)" }} />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent
              className="rounded-2xl"
              style={{ background: "var(--st-bg-surface)" }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle
                  style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                >
                  Regenerate Thumbnail?
                </AlertDialogTitle>
                <AlertDialogDescription
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                >
                  This will generate a new thumbnail for your story. You have{" "}
                  <span className="font-semibold" style={{ color: "var(--st-primary)" }}>
                    {regenerationsRemaining} regeneration{regenerationsRemaining === 1 ? '' : 's'}
                  </span>{" "}
                  remaining out of {THUMBNAIL_REGENERATION_LIMIT} total.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  className="rounded-lg"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    background: "var(--st-bg-muted)",
                    color: "var(--st-text-heading)",
                    border: "1px solid var(--st-border-light)",
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRegenerateThumbnail}
                  className="rounded-lg"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    background: "linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)",
                    color: "white",
                  }}
                >
                  Regenerate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-sm font-medium transition-colors bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                style={{ fontFamily: "Quicksand, sans-serif" }}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete Story
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent
              className="rounded-2xl"
              style={{ background: "var(--st-bg-page)" }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle
                  style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                >
                  Delete this story?
                </AlertDialogTitle>
                <AlertDialogDescription
                  style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
                >
                  This cannot be undone. "{story.title}" will be permanently
                  removed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  className="rounded-full"
                  style={{ fontFamily: "Quicksand, sans-serif" }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="rounded-full"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    background: "#DC2626",
                    color: "white",
                  }}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Created Date */}
          <div
            className="flex items-center justify-center gap-1.5 pt-2 text-xs"
            style={{ color: "var(--st-text-muted)" }}
          >
            <Calendar className="w-3 h-3" />
            <span style={{ fontFamily: "Quicksand, sans-serif" }}>
              Created{" "}
              {new Date(story.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StorySettingsModal;
